import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash.isempty';
import Button from '../Button/Button';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import { ReactComponent as PencilIcon } from '../../assets/images/priceAlerts/pencil.svg';
import { ReactComponent as TrashIcon } from '../../assets/images/priceAlerts/trash-no-fill.svg';
import DeleteAlertModal from './DeleteAlertModal';
import {
  EmptyState,
  EmptyStateIcon,
  PrimaryText,
  SecondaryText,
  UnstyledButton,
  List as BaseList,
} from './Common';
import { formatDate } from './Common/dateHelpers';

const List = styled(BaseList)`
  margin: 0 -${pxToRem(16)} ${pxToRem(16)} -${pxToRem(16)};
  width: calc(100% + ${pxToRem(32)});

  ${mediaBelow(variables.breakpoints.bpMd)} {
    margin: 0 -${pxToRemMd(16)} ${pxToRemMd(16)} -${pxToRemMd(16)};
    width: calc(100% + ${pxToRemMd(32)});
  }
`;

const Row = styled.li`
  padding: ${pxToRem(12)} ${pxToRem(16)};
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};

  &:last-child {
    border-bottom: none;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    padding: ${pxToRemMd(12)} ${pxToRemMd(16)};
  }
`;

const AlertPrice = styled.p`
  font-size: 16px;
  line-height: 140%;
  font-family: ${variables.family.goldSilver};
  margin: 0 0 ${pxToRem(4)} 0;
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const SecondLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AlertMeta = styled.span`
  font-size: 16px;
  line-height: 140%;
  font-family: ${variables.family.goldSilver};
  font-weight: 500;
  color: ${themeColors.colorTextSecondary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const IconGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(12)};
  flex-shrink: 0;
`;

const AddAnotherAlertWrapper = styled.div`
  button {
    font-family: ${variables.family.goldSilver};
    font-size: 16px;
    line-height: 140%;
    height: 44px;
    font-weight: 500;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    button {
      font-size: 14px;
    }
  }
`;

const ActionButton = styled(UnstyledButton)`
  display: flex;
  align-items: center;
  color: ${({ $readOnly }) =>
    $readOnly ? themeColors.colorDisabledIcon : themeColors.colorTextSecondary};

  svg {
    width: 24px;
    height: 24px;
  }

  ${({ $readOnly }) =>
    $readOnly &&
    `
    svg path {
      fill: ${themeColors.colorDisabledIcon};
    }
  `}

  &:hover {
    color: ${({ $readOnly }) =>
      $readOnly ? themeColors.colorDisabledIcon : themeColors.colorTextPrimary};
  }
`;

export const AddAnotherAlertButton = ({
  onAddAlert,
  isReadOnly,
  onBlockedAction,
}) => {
  const { t } = useTranslation();
  return (
    <AddAnotherAlertWrapper>
      <Button
        variant="secondaryDashed"
        isWidthS
        textTransform="none"
        label={t('gtt.common.addAnotherAlert')}
        lockStyle={isReadOnly}
        onClick={isReadOnly ? onBlockedAction : onAddAlert}
      />
    </AddAnotherAlertWrapper>
  );
};

AddAnotherAlertButton.propTypes = {
  onAddAlert: PropTypes.func,
  isReadOnly: PropTypes.bool,
  onBlockedAction: PropTypes.func,
};

const ActiveAlerts = ({
  userSetAlert,
  onEditAlert,
  onDeleteAlert,
  isReadOnly,
  onBlockedAction,
}) => {
  const { t } = useTranslation();
  const [alertToDelete, setAlertToDelete] = useState(null);

  const handleConfirmDelete = () => {
    if (alertToDelete && onDeleteAlert) {
      onDeleteAlert(alertToDelete);
    }
    setAlertToDelete(null);
  };

  if (!userSetAlert || isEmpty(userSetAlert)) {
    return (
      <EmptyState>
        <EmptyStateIcon />
        <PrimaryText>{t('gtt.labels.noActiveAlerts')}</PrimaryText>
        <SecondaryText>{t('gtt.labels.noActiveAlertsSub')}</SecondaryText>
      </EmptyState>
    );
  }

  return (
    <>
      <List>
        {userSetAlert.map((alert, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Row key={index}>
            <AlertPrice>{alert.alertPrice}</AlertPrice>
            <SecondLine>
              <AlertMeta>
                {t('gtt.labels.alertSetOn', {
                  date: formatDate(alert.alertDate),
                  method: alert.alertMethod,
                })}
              </AlertMeta>
              <IconGroup>
                <ActionButton
                  type="button"
                  $readOnly={isReadOnly}
                  aria-label={t('gtt.labels.editAlert')}
                  onClick={() =>
                    isReadOnly ? onBlockedAction?.() : onEditAlert?.(alert)
                  }
                >
                  <PencilIcon />
                </ActionButton>
                <ActionButton
                  type="button"
                  $readOnly={isReadOnly}
                  aria-label={t('gtt.labels.deleteAlert')}
                  onClick={() =>
                    isReadOnly ? onBlockedAction?.() : setAlertToDelete(alert)
                  }
                >
                  <TrashIcon />
                </ActionButton>
              </IconGroup>
            </SecondLine>
          </Row>
        ))}
      </List>
      <DeleteAlertModal
        open={!!alertToDelete}
        targetPrice={alertToDelete?.alertPrice}
        onCancel={() => setAlertToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

ActiveAlerts.propTypes = {
  userSetAlert: PropTypes.arrayOf(
    PropTypes.shape({
      PriceAlertUid: PropTypes.string,
      alertPrice: PropTypes.string,
      alertDate: PropTypes.string,
      alertMethod: PropTypes.string,
    }),
  ),
  onEditAlert: PropTypes.func,
  onDeleteAlert: PropTypes.func,
  isReadOnly: PropTypes.bool,
  onBlockedAction: PropTypes.func,
};

export default ActiveAlerts;
